import { generateState } from '../helpers.ts';
import { OAuthMessageType, OAuthPaths, type OAuthError } from '../protocol.ts';
import { noopLogger, type OAuthLogger } from '../logger.ts';
import type { IIncomingMessage, IMessageChannel, IPopupWindow, IWindowHost, MessageChannelFactory } from './types.ts';

const HANDSHAKE_TIMEOUT_MS = 30000;
const POPUP_POLL_INTERVAL_MS = 500;
const POPUP_FEATURES = 'width=600,height=700,scrollbars=yes,resizable=yes';

export interface OAuthPopupFlowOptions {
  logger?: OAuthLogger;
  host?: IWindowHost;
  messageChannelFactory?: MessageChannelFactory;
}

/** Shape of `event.data` for messages exchanged with the popup, narrowed once at the boundary. */
interface OAuthMessageData {
  type?: string;
  code?: string;
  state?: string;
  error?: string;
}

export class OAuthPopupFlow {

  private readonly _tpaBaseUrl: string;
  private readonly _tpaOrigin: string;
  private readonly _logger: OAuthLogger;
  private readonly _host: IWindowHost;
  private readonly _messageChannelFactory: MessageChannelFactory;

  private _isInProgress = false;
  private _popup: IPopupWindow | null = null;
  private _channel: IMessageChannel | null = null;
  private _pollTimer: ReturnType<typeof setInterval> | null = null;
  private _readyTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private _messageHandler: ((event: IIncomingMessage) => void) | null = null;
  private _readyPromise: Promise<void> | null = null;

  /**
   * @param tpaBaseUrl - The TPA (third-party auth) domain base URL.
   * @param options - `host`/`messageChannelFactory` defaults resolve lazily so the module
   *   has no top-level browser API references.
   */
  constructor(tpaBaseUrl: string, options?: OAuthPopupFlowOptions) {
    this._tpaBaseUrl = tpaBaseUrl.replace(/\/+$/, '');
    this._tpaOrigin = new URL(this._tpaBaseUrl).origin;
    this._logger = options?.logger ?? noopLogger;
    this._host = options?.host ?? requireGlobal(globalThis.window, 'window');
    this._messageChannelFactory = options?.messageChannelFactory ?? requireGlobal(globalThis.MessageChannel, 'MessageChannel');
  }

  // --- Public getters ---

  public get isInProgress(): boolean {
    return this._isInProgress;
  }

  /** The TPA root, which the backend turns into the provider `redirect_uri` by appending {@link OAuthPaths.CallbackPage}. */
  public getRedirectUrl(): string {
    return `${this._tpaBaseUrl}/`;
  }

  // --- Public methods ---

  /**
   * Opens the popup AND starts listening for OAUTH_READY immediately.
   * MUST be called directly in the user click handler (before any `await`)
   * to avoid browser popup blockers.
   */
  public openPopup(): void {
    if (this._isInProgress) {
      throw this.createError({ type: 'handshakeFailed', message: 'OAuth flow already in progress' });
    }

    this._isInProgress = true;

    const popupUrl = this._tpaBaseUrl + OAuthPaths.Init;
    const popup = this._host.open(popupUrl, '_blank', POPUP_FEATURES);
    if (!popup) {
      this._isInProgress = false;
      throw this.createError({ type: 'popupBlocked' });
    }
    this._popup = popup;
    this._logger.log('[OAuth] popup opened to', popupUrl);

    // Listen for OAUTH_READY now — the popup may signal before continueWithAuthUrl() is called.
    // The catch prevents an unhandled rejection if timeout/close fires before it's awaited there.
    this._readyPromise = this.waitForPopupReady(popup);
    void this._readyPromise.catch(() => { /* surfaced when awaited in continueWithAuthUrl */ });
  }

  /**
   * Continues the OAuth flow after the auth URL has been fetched.
   * Awaits the OAUTH_READY signal (listener was attached in openPopup),
   * sends the auth URL via MessageChannel, and waits for the result.
   *
   * @returns The authorization code on success.
   * @throws OAuthError on failure.
   */
  public async continueWithAuthUrl(authUrl: string): Promise<string> {
    const popup = this._popup;
    if (!popup || !this._readyPromise) {
      throw this.createError({ type: 'handshakeFailed', message: 'No popup open — call openPopup() first' });
    }

    try {
      const state = generateState();
      const openerOrigin = this._host.origin;
      const authUrlWithState = this.appendStateParam(authUrl, state);

      // 1. Wait for popup OAUTH_READY
      await this._readyPromise;

      // 2. Create MessageChannel and transfer port2 to the popup
      this._logger.log('[OAuth] sending port transfer to', this._tpaOrigin);
      this._channel = new this._messageChannelFactory();
      popup.postMessage(
        { type: OAuthMessageType.PortTransfer },
        this._tpaOrigin,
        [this._channel.port2],
      );

      // 3. Send the auth payload over the private channel
      this._channel.port1.postMessage({
        authUrl: authUrlWithState,
        openerOrigin,
        state,
      });

      // 4. Wait for OAUTH_RESULT or OAUTH_ERROR via regular postMessage
      const result = await this.waitForResult(popup, state);

      return result.code;
    } finally {
      this.cleanup();
    }
  }

  public cancel(): void {
    this.cleanup();
  }

  public dispose(): void {
    this.cleanup();
  }

  // --- Private methods ---

  private appendStateParam(authUrl: string, state: string): string {
    try {
      const url = new URL(authUrl);
      url.searchParams.set('state', state);
      return url.toString();
    } catch {
      const separator = authUrl.includes('?') ? '&' : '?';
      return `${authUrl}${separator}state=${encodeURIComponent(state)}`;
    }
  }

  private waitForPopupReady(popup: IPopupWindow): Promise<void> {
    return new Promise((resolve, reject) => {
      this._readyTimeoutId = setTimeout(() => {
        this.removeMessageHandler();
        reject(this.createError({ type: 'timeout' }));
      }, HANDSHAKE_TIMEOUT_MS);

      this.startPopupPoll(popup, () => {
        this.clearReadyTimeout();
        this.removeMessageHandler();
        reject(this.createError({ type: 'closedEarly' }));
      });

      this._messageHandler = (event: IIncomingMessage) => {
        if (event.source !== popup) return;
        const data = event.data as OAuthMessageData | null | undefined;
        this._logger.log('[OAuth] message received:', data?.type, 'origin:', event.origin);
        if (event.origin !== this._tpaOrigin) return;
        if (data?.type !== OAuthMessageType.Ready) return;
        this.clearReadyTimeout();
        this.stopPopupPoll();
        this.removeMessageHandler();
        this._logger.log('[OAuth] READY received, resolving');
        resolve();
      };

      this._host.addEventListener('message', this._messageHandler);
    });
  }

  private waitForResult(popup: IPopupWindow, expectedState: string): Promise<{ code: string }> {
    return new Promise((resolve, reject) => {
      this.startPopupPoll(popup, () => {
        this.removeMessageHandler();
        reject(this.createError({ type: 'closedEarly' }));
      });

      this._messageHandler = (event: IIncomingMessage) => {
        if (event.source !== popup) return;
        if (event.origin !== this._tpaOrigin) return;
        const { type, code, state, error } = (event.data as OAuthMessageData | null) ?? {};

        if (type === OAuthMessageType.Error) {
          this.removeMessageHandler();
          reject(this.createError({ type: 'providerError', message: error || 'Unknown error' }));
          return;
        }

        if (type !== OAuthMessageType.Result) return;

        if (state !== expectedState) {
          this.removeMessageHandler();
          reject(this.createError({ type: 'stateMismatch' }));
          return;
        }

        if (!code) {
          this.removeMessageHandler();
          reject(this.createError({ type: 'handshakeFailed', message: 'Result message missing code' }));
          return;
        }

        this.removeMessageHandler();
        resolve({ code });
      };

      this._host.addEventListener('message', this._messageHandler);
    });
  }

  private startPopupPoll(popup: IPopupWindow, onClosed: () => void): void {
    this.stopPopupPoll();
    this._pollTimer = setInterval(() => {
      if (popup.closed) {
        this.stopPopupPoll();
        onClosed();
      }
    }, POPUP_POLL_INTERVAL_MS);
  }

  private stopPopupPoll(): void {
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  private clearReadyTimeout(): void {
    if (this._readyTimeoutId) {
      clearTimeout(this._readyTimeoutId);
      this._readyTimeoutId = null;
    }
  }

  private removeMessageHandler(): void {
    if (this._messageHandler) {
      this._host.removeEventListener('message', this._messageHandler);
      this._messageHandler = null;
    }
  }

  private cleanup(): void {
    this._isInProgress = false;
    this._readyPromise = null;
    this.clearReadyTimeout();
    this.stopPopupPoll();
    this.removeMessageHandler();

    if (this._channel) {
      try {
        this._channel.port1.close();
      } catch {
        /* already closed */
      }
      try {
        this._channel.port2.close();
      } catch {
        /* already transferred or closed */
      }
      this._channel = null;
    }

    if (this._popup && !this._popup.closed) {
      try {
        this._popup.close();
      } catch (e) {
        this._logger.warn('Failed to close OAuth popup', e);
      }
    }
    this._popup = null;
  }

  private createError(oauthError: OAuthError): Error {
    const error = new Error(`OAuth error: ${oauthError.type}`);
    (error as OAuthFlowError).oauthError = oauthError;
    return error;
  }
}

function requireGlobal<T>(value: T | undefined, name: string): T {
  if (value === undefined) {
    throw new Error(`OAuthPopupFlow: no host/messageChannelFactory option provided and global ${name} is unavailable`);
  }
  return value;
}

export type OAuthFlowError = Error & { oauthError: OAuthError };

export function isOAuthFlowError(error: unknown): error is OAuthFlowError {
  return error instanceof Error && 'oauthError' in error;
}
